<!-- /
 Custom numeric input component.
 
 - Accepts `modelValue` as Number or String (with dot `.` as decimal separator).
 - Always emits a valid Number or `null` (never a string).
 - Only allows typing numbers and a dot — blocks invalid characters like letters or commas.
 - Rejects pasted input if it doesn't match the numeric format.
 
 Useful for clean numeric input with strict dot-based decimals,
 avoiding locale-specific quirks of <input type="number"/>.
 / -->

<template>
  <b-form-input v-bind="$attrs" v-model="inputValue" type="text" @keypress="blockNonNumeric" @paste="handlePaste" />
</template>

<script setup>
import { ref, watch } from 'vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: '',
  },
});

const regex = /^[+-]?(\d+(\.\d*)?|\.\d+)?([eE][+-]?\d+)?$/;

const inputValue = ref('');

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue == null || newValue === '') {
      inputValue.value = '';

      return;
    }

    const s = String(newValue);

    if (typeof newValue === 'string' && regex.test(s) && Number.isNaN(Number(s))) {
      // invalid string → clear & emit null
      inputValue.value = s;

      return;
    }

    // numeric string → convert upstream & show raw
    if (typeof newValue === 'string' && regex.test(s)) {
      const n = Number(s);
      emit('update:modelValue', n);
      inputValue.value = s;

      return;
    }

    // real Number → just stringify
    if (typeof newValue === 'number') {
      inputValue.value = s;

      return;
    }

    // anything else → clear & emit null
    inputValue.value = '';

    emit('update:modelValue', null);
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  () => inputValue.value,
  () => {
    if (inputValue.value === '') {
      emit('update:modelValue', null);
    } else if (
      regex.test(inputValue.value) &&
      !Number.isNaN(Number(inputValue.value)) &&
      inputValue.value == Number(inputValue.value).toString()
    ) {
      emit('update:modelValue', Number(inputValue.value));
    }
  },
);

const blockNonNumeric = (event) => {
  const cursorPosition = event.target.selectionStart;
  const currentValue = inputValue.value;
  const newValue = currentValue.slice(0, cursorPosition) + event.key + currentValue.slice(cursorPosition);

  if (!regex.test(newValue)) {
    event.preventDefault();
  }
}

const handlePaste = (event) => {
  const pastedData = event.clipboardData.getData('text');

  if (!regex.test(pastedData)) {
    event.preventDefault();
    return;
  }
}
</script>
